import { useCallback, useEffect, useRef } from "react"
import useElmish, {
  Reducer,
  ReducerStateEffectPair,
  Dispatch,
  ReducerAction,
  ReducerState,
} from "react-use-elmish"

export const useAbortController = () => {
  const abortController = useRef<AbortController | null>(null)

  if (abortController.current === null) {
    abortController.current = new AbortController()
  }

  useEffect(() => {
    return () => {
      abortController.current?.abort("Request aborted, component has unmounted")
    }
  }, [])

  return abortController.current
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useElmishWithAbort<R extends Reducer<any, any>>(
  reducer: (cancellationToken: AbortSignal) => R,
  initializer: () => ReducerStateEffectPair<R>
): [ReducerState<R>, Dispatch<ReducerAction<R>>] {
  const cancellationToken = useAbortController()

  const update = useCallback(
    (state: ReducerState<R>, action: ReducerAction<R>) => {
      return reducer(cancellationToken.signal)(state, action)
    },
    [cancellationToken.signal, reducer]
  )

  return useElmish(update, initializer)
}
